import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Bell, BellOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  // Defensive check and trim any whitespace or trailing equals
  const cleanString = (base64String || '').trim().replace(/=+$/, '');
  
  const padding = '='.repeat((4 - (cleanString.length % 4)) % 4);
  const base64 = (cleanString + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');
  
  try {
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    // Debug logging for developers (will only show in browser console)
    if (outputArray.length !== 65) {
      console.warn(`VAPID Public Key length is ${outputArray.length} bytes, expected 65.`);
    }
    return outputArray;
  } catch (err) {
    console.error('Base64 decode failed for VAPID key:', err);
    throw new Error('Kodim i pasaktë i çelësit VAPID');
  }
}

const PrayerNotifications = () => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [permission, setPermission] = useState('default');
  const lastNotifiedRef = React.useRef(null);

  useEffect(() => {
    // Check notification permission status
    if ('Notification' in window) {
      setPermission(Notification.permission);
      const enabled = localStorage.getItem('ezanNotifications') === 'true';
      setNotificationsEnabled(enabled && Notification.permission === 'granted');
    }

    const handleActivated = () => {
      setNotificationsEnabled(true);
      localStorage.setItem('ezanNotifications', 'true');
      subscribeToPush();
      showNotification('✅ Njoftimet u aktivizuan', 'Do të merrni njoftime për çdo namaz.');
    };
    window.addEventListener('ezanNotificationsActivated', handleActivated);
    return () => window.removeEventListener('ezanNotificationsActivated', handleActivated);
  }, []);

  const subscribeToPush = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const reg = await navigator.serviceWorker.ready;
        
        // Force unsubscribe from any existing subscription to avoid VAPID key mismatch
        const existingSub = await reg.pushManager.getSubscription();
        if (existingSub) {
          console.log('Unsubscribing from existing push to refresh keys...');
          await existingSub.unsubscribe();
        }

        // Use env var or the guaranteed fallback key
        const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || 'BNdrpd63FYNIezV1kx8XnbsyaWz3k3Jhwh23xhptIXWg6xRpZFBnZuhDlFJwv_64akwty5vf88S7EGEZ1lIUBjE';
        
        if (!vapidKey) return;

        const subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });

        const subscriptionData = subscription.toJSON();
        console.log('Sending subscription to backend:', subscriptionData);
        
        await axios.post(`${API}/notifications/subscribe`, {
          subscription: subscriptionData,
          timestamp: new Date().toISOString()
        });
        console.log('Push notification subscribed successfully');
      } catch (e) {
        console.error('Failed to subscribe to push API:', e);
        const status = e.response?.status;
        const detail = e.response?.data?.error || e.message;
        toast.error(`Dështoi regjistrimi: ${status ? status : ''} ${detail}`);
      }
    }
  };

  const showNotification = async (title, body, playSound = false) => {
    if (Notification.permission === 'granted') {
      const options = {
        body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: 'prayer-time',
        requireInteraction: playSound,
      };

      if ('serviceWorker' in navigator) {
        try {
          const reg = await navigator.serviceWorker.ready;
          await reg.showNotification(title, options);
        } catch (error) {
          console.error('Service worker notification failed, falling back to window Notification', error);
          const notification = new Notification(title, options);
          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        }
      } else {
        const notification = new Notification(title, options);
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }

      if (playSound) {
        // Play a subtle notification sound (you can replace with actual ezan if needed)
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGGi78OScTgwNUrDn77RlGwU7k9rxw3ksBS2A0fDajjcHGWm98Oacjw==');
        audio.play().catch(e => console.log('Audio play failed:', e));
      }
    }
  };

  const toggleNotifications = async () => {
    if (!('Notification' in window)) {
      toast.error('Shfletuesi juaj nuk mbështet njoftimet');
      return;
    }

    if (Notification.permission === 'denied') {
      toast.error('Njoftimet janë bllokuar. Aktivizojini nga settings e shfletuesit.');
      return;
    }

    if (Notification.permission === 'default') {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        setNotificationsEnabled(true);
        localStorage.setItem('ezanNotifications', 'true');
        toast.success('Njoftimet e ezanit janë aktivizuar! 🕌');
        subscribeToPush();
        
        // Show test notification
        showNotification(
          '✅ Njoftimet u aktivizuan',
          'Do të merrni njoftime 5 minuta para dhe në kohën e çdo namazi'
        );
      } else {
        toast.error('Njoftimet u refuzuan');
      }
    } else if (notificationsEnabled) {
      setNotificationsEnabled(false);
      localStorage.setItem('ezanNotifications', 'false');
      toast.info('Njoftimet e ezanit u çaktivizuan');
    } else {
      setNotificationsEnabled(true);
      localStorage.setItem('ezanNotifications', 'true');
      toast.success('Njoftimet e ezanit janë aktivizuar! 🕌');
      subscribeToPush();
    }
  };

  return (
    <button
      onClick={toggleNotifications}
      className={`fixed bottom-24 right-6 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 z-40 ${
        notificationsEnabled
          ? 'bg-accent text-white'
          : 'bg-white text-muted-foreground border-2 border-muted'
      }`}
      title={notificationsEnabled ? 'Çaktivizo njoftimet e ezanit' : 'Aktivizo njoftimet e ezanit'}
      data-testid="notification-toggle"
    >
      {notificationsEnabled ? (
        <Bell className="w-6 h-6" fill="white" />
      ) : (
        <BellOff className="w-6 h-6" />
      )}
    </button>
  );
};

export default PrayerNotifications;
